import { useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import {
    getJumpHeightUnit,
    getLiveRefreshInterval,
    getRotationLabels,
    getRotationNaming,
    kJumpHeightUnitStorage,
    kLiveRefreshIntervalStorage,
    kRotationNamingStorage,
} from "../utils/Constants";

// General application options (non-theme settings).
function OptionsPanel() {
  const { t } = useLanguage();
  const [rotationNaming, setRotationNaming] = useState(getRotationNaming());
  const [refreshInterval, setRefreshInterval] = useState(getLiveRefreshInterval());
  const [jumpHeightUnit, setJumpHeightUnit] = useState(getJumpHeightUnit());

  const saveRotationNaming = (value) => {
    setRotationNaming(value);
    localStorage.setItem(kRotationNamingStorage, value);
  };

  const saveJumpHeightUnit = (value) => {
    setJumpHeightUnit(value);
    localStorage.setItem(kJumpHeightUnitStorage, value);
  };

  const saveRefreshInterval = (value) => {
    const n = parseInt(value);
    if (!isNaN(n) && n >= 5) {
      setRefreshInterval(n);
      localStorage.setItem(kLiveRefreshIntervalStorage, String(n));
    }
  };

  const exampleLabels = getRotationLabels(rotationNaming);

  return (
    <div className="flex flex-col gap-6 p-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-bold mb-1">{t("Rotation naming convention")}</h2>
        <p className="text-sm text-base-content/70 mb-3">
          {t("Choose how rotations are labelled in reports and analysis.")}
        </p>

        <div className="flex flex-col gap-3">
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-base-300 p-3 hover:bg-base-200">
            <input
              type="radio"
              name="rotation-naming"
              className="radio radio-sm mt-0.5"
              checked={rotationNaming === "R"}
              onChange={() => saveRotationNaming("R")}
            />
            <div>
              <p className="font-semibold">{t("R1–R6 (rotation sequence)")}</p>
              <p className="text-xs text-base-content/70">
                {t(
                  "R1: setter in position 1, R2: setter in 6, R3: setter in 5, R4: setter in 4, R5: setter in 3, R6: setter in 2."
                )}
              </p>
            </div>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-base-300 p-3 hover:bg-base-200">
            <input
              type="radio"
              name="rotation-naming"
              className="radio radio-sm mt-0.5"
              checked={rotationNaming === "S"}
              onChange={() => saveRotationNaming("S")}
            />
            <div>
              <p className="font-semibold">{t("S1–S6 (setter court position)")}</p>
              <p className="text-xs text-base-content/70">
                {t(
                  "S1: setter in 1, S2: setter in 2, S3: setter in 3, S4: setter in 4, S5: setter in 5, S6: setter in 6."
                )}
              </p>
            </div>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-base-content/70">{t("Labels:")}</span>
          {exampleLabels.map((label) => (
            <span key={label} className="badge badge-outline">
              {label}
            </span>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold mb-1">{t("Live session refresh interval")}</h2>
        <p className="text-sm text-base-content/70 mb-3">
          {t("How often (in seconds) the events list is updated during a live session.")}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="5"
            max="300"
            value={refreshInterval}
            onChange={(e) => saveRefreshInterval(e.target.value)}
            className="input input-sm input-bordered w-24"
          />
          <span className="text-sm text-base-content/70">{t("seconds")} (min 5)</span>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold mb-1">{t("Jump height units")}</h2>
        <p className="text-sm text-base-content/70 mb-3">
          {t("Choose how Catapult jump heights are displayed and filtered.")}
        </p>
        <div className="flex gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 p-3 hover:bg-base-200">
            <input
              type="radio"
              name="jump-height-unit"
              className="radio radio-sm"
              checked={jumpHeightUnit === "cm"}
              onChange={() => saveJumpHeightUnit("cm")}
            />
            <span className="font-semibold">{t("Centimetres (cm)")}</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 p-3 hover:bg-base-200">
            <input
              type="radio"
              name="jump-height-unit"
              className="radio radio-sm"
              checked={jumpHeightUnit === "in"}
              onChange={() => saveJumpHeightUnit("in")}
            />
            <span className="font-semibold">{t("Inches (in)")}</span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default OptionsPanel;
